import { CartLineItem } from 'domains/cartDomain';
import {
  AddSharedParam,
  BasicUpdateParam,
  ChangeJobNameParam,
  CompareWithOrgParam,
  CreateShippingParam,
  DeleteSharedParam,
  JobConfig,
  JobConfigQuantityItemShippingCalendar,
  JobConfigShippingCalendar,
  JobConfigSvcOptionType,
  JobConfigViewer,
  JobOptionSvc,
  JobSpec,
  JobSpecItemExpression,
  JobSvcOption,
  MaterialsBasePaperOption,
  OptionPaperCharges,
  OptionPaperChargesList,
  OptionPapers,
  OptionPapersList,
  QuantityPayload,
  SearchCalendarParam,
  SendSharedListParam,
  SkuDynamicApproveResponseDTO,
  SkuDynamicPreviewResponseDTO,
  SkuDynamicSaveParamsDTO,
  StartJobConfigParam,
  UpdateJobConfigVersion,
  UpdateJobSpecParam,
  UpdateShippingParam,
} from 'domains/jobconfigDomain';
import { ProofTypeEnum } from 'domains/workspaceDetailDomain';
import { JobItem } from 'domains/workspaceDomain';
import api from 'utils/api';
const jobconfigService = {
  getJobSpec: async (skuId: number) => {
    return api.get<JobSpec>(`/api/job-sku/${skuId}`);
  },

  getJobOptionSvcs: async (
    isWrapper: boolean,
    targetSkuId: number,
    wrapperSkuId: number
  ) => {
    return api.post<JobOptionSvc>(`/api/job-sku/options/svcs`, {
      isWrapper,
      targetSkuId,
      wrapperSkuId,
    });
  },

  startJobConfig: (params: StartJobConfigParam) => {
    return api.post<JobConfig>(`/job-config/start`, params);
  },

  directlyAddToCart: (params: StartJobConfigParam) => {
    return api.post<CartLineItem>(`/job-config/directly-add-to-cart`, params);
  },

  summerStartJobConfig: (params: Partial<StartJobConfigParam>) => {
    return api.post<JobConfigViewer>(`/job-config/summary/start`, params);
  },

  summerUpdateJobConfig: (params: Partial<JobConfigViewer>) => {
    return api.put<JobConfigViewer>(`/job-config/summary/${params.id}`, params);
  },

  summerAddToCart: (params: Partial<JobConfigViewer>) => {
    return api.post<CartLineItem>(
      `/job-config/summary/${params.id}/add-to-cart`
    );
  },

  summeryDeleteJobConfig: (id: string) => {
    return api.delete<JobConfigViewer>(`/job-config/summary/${id}`);
  },

  getJobConfigBySkuId: (skuId: number | string) => {
    return api.post<JobItem>(`/job-config/default?skuId=${skuId}`);
  },

  updateVersion: (payload: UpdateJobConfigVersion) => {
    return api.put<JobConfig>(`/job-config/version`, payload);
  },

  getJobConfigFromCache: (id: string) => {
    return api.get<JobConfig>(`/job-config/cache/${id}`);
  },

  addQuantity: (payload: Partial<QuantityPayload>) => {
    return api.post<JobConfig>(`/job-config/add-quantity`, payload);
  },

  updateQuantity: (payload: Partial<QuantityPayload>) => {
    return api.put<JobConfig>(`/job-config/update-quantity`, payload);
  },

  deleteQuantity: (payload: Partial<QuantityPayload>) => {
    return api.delete<JobConfig>(`/job-config/delete-quantity`, payload);
  },

  updateJobSpec: (
    payload: UpdateJobSpecParam,
    configId: string,
    dryRunEnabled: boolean = false
  ) => {
    return api.put<JobConfig>(
      `/job-config/update/${configId}?dryRunEnabled=${dryRunEnabled}`,
      payload
    );
  },

  getOptionPaper: (payload: Partial<OptionPapers>) => {
    return api.post<OptionPapersList[]>(`/job-sku/option-papers`, payload);
  },

  getOptionPaperCharges: (payload: Partial<OptionPaperCharges>) => {
    return api.post<OptionPaperChargesList[]>(
      `/job-sku/option-papers/charges`,
      payload
    );
  },

  getShippingCalendar: (configId: string) => {
    return api.get<JobConfigQuantityItemShippingCalendar[]>(
      `/job-config/shipping/calendars/${configId}`
    );
  },

  getOptionSvcs: ({ data, ...payload }: JobSvcOption) => {
    return api.post<JobConfigSvcOptionType>(`/job-sku/option-svcs`, payload);
  },

  createShipping: (payload: CreateShippingParam) => {
    return api.post<JobConfig>('/job-config/shipping/create', payload);
  },

  updateShipping: (payload: UpdateShippingParam) => {
    return api.post<JobConfig>('/job-config/shipping/update', payload);
  },

  getMaterialsBasePaperOptions: () => {
    return api.get<MaterialsBasePaperOption>(
      `/api/materials/base/papers/options`
    );
  },

  searchCalendar: (payload: SearchCalendarParam) => {
    return api.post<JobConfigShippingCalendar[]>(
      '/job-config/shipping/search/calendars',
      payload
    );
  },

  getPriceExpression: (jobConfigId: string) => {
    return api.get<JobSpecItemExpression[]>(
      `/job-config/expression/${jobConfigId}`
    );
  },

  getCompareWithOrg: (compareWithOrgParam: CompareWithOrgParam) => {
    return api.get(
      `/job-config/compare-with-org/${compareWithOrgParam.jobConfigId}`,
      compareWithOrgParam
    );
  },

  addShared: (addSharedParam: AddSharedParam) => {
    return api.post<JobConfig>(
      `/job-config/shared/add/${addSharedParam.jobConfigId}?serialNumbers=${addSharedParam.serialNumbers}`,
      addSharedParam
    );
  },

  deleteShared: (deleteSharedParam: DeleteSharedParam) => {
    return api.delete<JobConfig>(
      `/job-config/shared/delete/${deleteSharedParam.jobConfigId}?email=${deleteSharedParam.email}`,
      deleteSharedParam
    );
  },

  sendShareList: (sendSharedListParam: SendSharedListParam) => {
    return api.post(
      `/job-config/shared/send/${
        sendSharedListParam.jobConfigId
      }?serialNumbers=${
        sendSharedListParam.serialNumbers
      }&emails=${sendSharedListParam.emails?.map((item) =>
        encodeURIComponent(item)
      )}`,
      sendSharedListParam
    );
  },

  updateJobRemark: (jobConfigId: string, remark: string) => {
    return api.post<JobConfig>(
      `/job-config/update-remark/${jobConfigId}`,
      remark,
      {
        'Content-Type': 'text/plain',
      }
    );
  },

  changeProofType: (jobConfigId: string, proofType: ProofTypeEnum) => {
    return api.put<JobConfig>(
      `/job-config/proof-type/${jobConfigId}?proofType=${proofType}`
    );
  },

  addFileToJobConfig: (
    fileName: string,
    fileUrl: string,
    jobConfigId: string,
    skuId?: number
  ) => {
    return api.post<JobConfig>(`/job-config/files`, {
      fileName,
      fileUrl,
      jobConfigId,
      skuId,
    });
  },

  deleteFile: (jobConfigId: string, fileId: string) => {
    return api.delete<JobConfig>(`/job-config/files/${jobConfigId}/${fileId}`);
  },

  changeJobName: (payload: ChangeJobNameParam) => {
    return api.put<JobConfig>(
      `/job-config/basic-update/${payload.jobConfigId}`,
      {
        projectName: payload.name,
      }
    );
  },

  addJobQuantityToCart: (jobConfigId: string, serialNumber: number) => {
    return api.post<CartLineItem>(
      `/job-config/${jobConfigId}/add-to-cart?serialNumber=${serialNumber}`
    );
  },

  basicUpdate: (jobConfigId: string, param: Partial<BasicUpdateParam>) => {
    return api.put<JobConfig>(`/job-config/basic-update/${jobConfigId}`, param);
  },

  confirmUpdateToCart: (jobId: string) => {
    return api.post<CartLineItem>(`/job-config/confirm-to-cart/${jobId}`);
  },

  jobConfigDelete: (jobConfigId: string) => {
    return api.delete(`/job-config/${jobConfigId}`);
  },

  editInCart: (jobConfigId: number) => {
    return api.post<JobConfig>(`/job-config/edit-in-cart/${jobConfigId}`);
  },

  sendToSourcer: (jobConfigId: string) => {
    return api.post(`/job-config/send-to-sourcer/${jobConfigId}`);
  },

  jobRevisionComplete: (revisionId: number) => {
    return api.post(`/job-revision/complete?revisionId=${revisionId}`);
  },

  jobRevisionAdd: (jobId: number) => {
    return api.post(`/job-revision/add?jobId=${jobId}`);
  },

  jobRevisionCancel: (revisionId: number) => {
    return api.post(`/job-revision/cancel?revisionId=${revisionId}`);
  },

  completeFromEstimator: (
    revisionId: number,
    comparatorItemSerialNumber: number,
    quantityItemSerialNumber: number
  ) => {
    return api.post(
      `/job-revision/complete-from-estimator?revisionId=${revisionId}&comparatorItemSerialNumber=${comparatorItemSerialNumber}&quantityItemSerialNumber=${quantityItemSerialNumber}`
    );
  },

  previewSkuDynamic: (skuDynamicSaveParams: SkuDynamicSaveParamsDTO) => {
    return api.post<SkuDynamicPreviewResponseDTO>(
      `/jobs/sku/dynamic/params/preview`,
      skuDynamicSaveParams
    );
  },

  approveSkuDynamic: (skuDynamicSaveParams: SkuDynamicSaveParamsDTO) => {
    return api.post<SkuDynamicApproveResponseDTO>(
      `/jobs/sku/dynamic/params/approve`,
      skuDynamicSaveParams
    );
  },
};
export default jobconfigService;
